import {DialogFooter, Form, FormButton, FormField} from 'platform/components';
import {Grid, HStack, Show, VStack} from 'platform/foundation';
import {z} from 'zod';

import {type FeatureResponseBody, type TrimFeatureAssignment} from '@dms/api/new-vehicle';
import {getNewVehicleEnumLabel} from '@dms/features/new-vehicle';

import type {Nullish} from 'shared/web';

import {handleCatalogueError, t} from './catalogueUi';
import {GrossPriceField} from './GrossPriceField';
import {optionalMoneyInput, optionalPriceInput} from './moneyInput';
import {NewPricelistVersionDialog} from './NewPricelistVersionDialog';
import {NumberField} from './NumberField';
import {PriceRow} from './PriceRow';
import {usePublishedPriceSave} from './usePublishedPriceSave';

const assignmentSchema = z.object({
  inclusion: z.enum(['STANDARD', 'OPTIONAL']),
  priceDeltaNet: optionalPriceInput,
  costNet: optionalMoneyInput,
  sortOrder: z.coerce.number().int().nonnegative(),
  anyOfVariantIds: z.array(z.string()),
  allOfFeatureIds: z.array(z.string()),
});
export type AssignmentValues = z.infer<typeof assignmentSchema>;

export function AssignmentDialog(props: {
  modelYearId: string;
  assignment: TrimFeatureAssignment;
  features: FeatureResponseBody[];
  variants: {id: string; orderCode: string}[];
  canViewCost: boolean;
  vatRate: string | Nullish;
  save: (values: AssignmentValues) => Promise<void>;
  onDone: () => void;
}) {
  const publishedPrice = usePublishedPriceSave(props.modelYearId);
  const defaultValues: AssignmentValues = {
    inclusion: props.assignment.inclusion,
    priceDeltaNet: props.assignment.priceDeltaNet ?? '',
    costNet: props.assignment.costNet ?? '',
    sortOrder: props.assignment.sortOrder,
    anyOfVariantIds: props.assignment.appliesIf.anyOfVariantIds,
    allOfFeatureIds: props.assignment.appliesIf.allOfFeatureIds,
  };
  const submit = async (values: AssignmentValues) => {
    await publishedPrice
      .save({
        values,
        storedValues: defaultValues,
        priceField: 'priceDeltaNet',
        scope: 'trimFeature',
        resourceId: props.assignment.priceTargetId,
        save: props.save,
        onDone: props.onDone,
      })
      .catch(handleCatalogueError);
  };
  return (
    <>
      <Form<AssignmentValues>
        experimentalZodSchema={assignmentSchema}
        defaultValues={defaultValues}
        onSubmit={submit}
      >
        {(control) => (
          <VStack spacing={4}>
            <Grid columns={2} spacing={3}>
              <FormField
                control={control}
                name="inclusion"
                type="choice"
                menuInPortal
                label={t('entity.newVehicle.catalogue.inclusion', 'Inclusion')}
                options={['STANDARD', 'OPTIONAL'].map((value) => ({
                  value,
                  label: getNewVehicleEnumLabel('inclusion', value, t),
                }))}
              />
              <FormField
                control={control}
                name="sortOrder"
                type="number"
                label={t('entity.newVehicle.catalogue.sortOrder', 'Sort order')}
              />
              <PriceRow
                vatRate={props.vatRate}
                canViewCost={props.canViewCost}
                spacing={3}
                span={2}
              >
                <GrossPriceField
                  control={control}
                  name="priceDeltaNet"
                  vatRate={props.vatRate}
                  label={t('entity.newVehicle.catalogue.priceDeltaNet', 'Price delta · net')}
                  grossLabel={t(
                    'entity.newVehicle.catalogue.priceDeltaGrossAtRate',
                    'Price delta · gross ({rate} % VAT)',
                    {rate: props.vatRate}
                  )}
                />
                <Show when={props.canViewCost}>
                  <NumberField
                    control={control}
                    name="costNet"
                    label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
                  />
                </Show>
              </PriceRow>
              <FormField
                control={control}
                name="anyOfVariantIds"
                type="multiChoice"
                menuInPortal
                label={t('entity.newVehicle.catalogue.appliesVariants', 'Applies to variants')}
                options={props.variants.map(({id, orderCode}) => ({value: id, label: orderCode}))}
              />
              <FormField
                control={control}
                name="allOfFeatureIds"
                type="multiChoice"
                menuInPortal
                label={t(
                  'entity.newVehicle.catalogue.appliesFeatures',
                  'Applies when features selected'
                )}
                options={props.features.map(({id, name}) => ({value: id, label: name}))}
              />
            </Grid>
            <DialogFooter>
              <HStack justify="flex-end">
                <FormButton
                  control={control}
                  type="button"
                  title={t('general.actions.save', 'Save')}
                  onClick={() => void control.submitForm()}
                />
              </HStack>
            </DialogFooter>
          </VStack>
        )}
      </Form>
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
    </>
  );
}
